import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { InputSwitch } from "primereact/inputswitch";
import { useNegotiationsStore } from "../../../stores/negotiationsStore";
import {
	useFeatureFlagsStore,
	FeatureFlag,
} from "../../../stores/featureFlagsStore";
import "./ReviewSection.scss";

const FEATURE_FLAG_LABELS: Record<string, { label: string; description: string }> = {
	[FeatureFlag.VISUALIZATION_AGENT]: {
		label: "Visualization Agent",
		description: "Enable AI-powered data visualization in chat",
	},
	[FeatureFlag.CUSTOM_TAB]: {
		label: "Custom Tab",
		description: "Enable custom analysis tabs",
	},
};

const ReviewSection: React.FC = () => {
	const { data } = useNegotiationsStore();
	const { negotiation_id } = useParams<{ negotiation_id: string }>();
	const [developerSettingsOpen, setDeveloperSettingsOpen] = useState(false);

	const {
		featureFlags,
		availableFlags,
		loading,
		fetchFeatureFlags,
		fetchAvailableFlags,
		setFeatureFlag,
	} = useFeatureFlagsStore();

	const negotiationId = negotiation_id ? parseInt(negotiation_id, 10) : null;

	useEffect(() => {
		if (negotiationId) {
			fetchFeatureFlags(negotiationId);
			fetchAvailableFlags();
		}
	}, [negotiationId, fetchFeatureFlags, fetchAvailableFlags]);

	const handleToggleFeature = async (flag: string, enabled: boolean) => {
		if (negotiationId) {
			await setFeatureFlag(negotiationId, flag, enabled);
		}
	};

	return (
		<div className="review-section">
			<p className="review-title my-0">Summary</p>
			<div className="detail-section">
				<p className="my-0">Form</p>
			</div>
			<div className="form-section">
				<p className="my-0 question">
					1. Is this negotiation for private label or branded
					articles?
				</p>
				<p className="my-0 answers">Private Label - {data?.brand}</p>

				<p className="my-0 question">2. Target for negotiations</p>
				<p className="my-0 answers">
					Target actual savings: {data?.target_savings} %
				</p>
				<p className="my-0 answers">
					Target simulated savings: {data?.target_simulated_savings} %
				</p>

				<p className="my-0 question">
					3. Other category goals: (E.g. C&R)
				</p>
				<p className="my-0 answers">{data?.extra_goals}</p>
			</div>

			{/* Developer Settings Accordion */}
			<div
				className={`developer-settings-accordion ${developerSettingsOpen ? "is-open" : ""}`}
			>
				<button
					type="button"
					className="developer-settings-toggle"
					onClick={() => setDeveloperSettingsOpen((prev) => !prev)}
				>
					<span className="toggle-label">
						<i className="pi pi-cog" />
						Developer Settings
					</span>
					<i
						className={`pi ${developerSettingsOpen ? "pi-chevron-up" : "pi-chevron-down"}`}
					/>
				</button>
				{developerSettingsOpen && (
					<div className="developer-settings-content">
						<p className="settings-description">
							Configure feature flags for this negotiation. These
							settings control experimental features.
						</p>
						<div className="feature-flags-list">
							{availableFlags.map((flag) => {
								const flagInfo = FEATURE_FLAG_LABELS[flag] || {
									label: flag,
									description: "",
								};
								const isEnabled = featureFlags.includes(flag);

								return (
									<div
										key={flag}
										className="feature-flag-item"
									>
										<div className="feature-flag-info">
											<span className="feature-flag-label">
												{flagInfo.label}
											</span>
											{flagInfo.description && (
												<span className="feature-flag-description">
													{flagInfo.description}
												</span>
											)}
										</div>
										<InputSwitch
											checked={isEnabled}
											onChange={(e) =>
												handleToggleFeature(
													flag,
													e.value
												)
											}
											disabled={loading}
										/>
									</div>
								);
							})}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default ReviewSection;
